import React from 'react';
import { ChartSchema } from '../../../types/types';

interface ScatterChartRendererProps {
  series: ChartSchema['series'];
  xScale: (value: string | number) => number;
  yScale: (value: number) => number;
}

/**
 * Scatter Chart Renderer Component for outlier visualization
 */
export const ScatterChartRenderer: React.FC<ScatterChartRendererProps> = ({ series, xScale, yScale }) => {
  return (
    <>
      {/* Optional connecting lines between points */}
      {series.map(series => {
        if (series.connectPoints) {
          const linePath = series.data
            .map((point, index) => {
              const x = xScale(point.x);
              const y = yScale(point.y);
              return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
            })
            .join(' ');

          return (
            <path
              key={`line-${series.id}`}
              d={linePath}
              fill="none"
              stroke={series.color}
              strokeWidth={1}
              strokeDasharray="3 3"
              opacity={0.7}
            />
          );
        }
        return null;
      })}

      {/* Data points with different sizes based on importance */}
      {series.map(series =>
        series.data.map((point, i) => {
          // Calculate point size - default is 4, but can be specified in data
          const pointSize = point.size || 4;
          const isOutlier = point.isOutlier || false;

          return (
            <g key={`point-${series.id}-${i}`}>
              {/* Highlight circle for outliers */}
              {isOutlier && (
                <circle
                  cx={xScale(point.x)}
                  cy={yScale(point.y)}
                  r={pointSize + 3}
                  fill="none"
                  stroke={series.color}
                  strokeWidth={1}
                  opacity={0.5}
                />
              )}

              {/* Main data point */}
              <circle
                cx={xScale(point.x)}
                cy={yScale(point.y)}
                r={pointSize}
                fill={isOutlier ? series.color : 'white'}
                stroke={series.color}
                strokeWidth={isOutlier ? 0 : 2}
              />
            </g>
          );
        })
      )}
    </>
  );
};

export default ScatterChartRenderer;
